import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './createListing.css';
import { useAuth } from '../../AuthContext';
import { db, functions } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { formatLongDateTime } from '../../utils/dateFormat';
import { BOOKING_CUTOFF_OPTIONS } from '../../constants/listingOptions';
import {
  TitleField,
  DescriptionField,
  MenuField,
  DietaryField,
  AlcoholField,
  SpecialNotesField,
} from '../../components/MealFormFields';

const EditListing = () => {
  const { currentUser } = useAuth();
  const { mealId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    menu: '',
    maxGuests: '',
    dietaryAccommodations: [],
    alcoholOption: 'none',
    bookingCutoff: '',
    specialNotes: ''
  });

  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchMeal = async () => {
      if (!mealId) {
        setError('No meal ID provided');
        setLoading(false);
        return;
      }

      try {
        const mealDoc = await getDoc(doc(db, 'meals', mealId));
        if (!mealDoc.exists()) {
          setError('Meal not found');
          setLoading(false);
          return;
        }

        const mealData = mealDoc.data();

        if (mealData.hostId !== currentUser?.uid) {
          setError('You do not have permission to edit this meal');
          setLoading(false);
          return;
        }

        // Street/unitNumber live on the private address subdoc (F-06),
        // readable here because rules grant the host direct read access.
        const addressDoc = await getDoc(doc(db, 'meals', mealId, 'private', 'address'));
        const addressData = addressDoc.exists() ? addressDoc.data() : {};

        setOriginalData({ ...mealData, ...addressData });
        setFormData({
          title: mealData.title || '',
          description: mealData.description || '',
          menu: mealData.menu || '',
          maxGuests: mealData.maxGuests?.toString() || '',
          dietaryAccommodations: mealData.dietaryAccommodations || [],
          alcoholOption: mealData.alcoholOption || 'none',
          bookingCutoff: mealData.bookingCutoff?.toString() || '',
          specialNotes: mealData.specialNotes || ''
        });
      } catch (err) {
        console.error('Error fetching meal:', err);
        setError('Failed to load meal data');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchMeal();
    }
  }, [mealId, currentUser]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (formData.menu.length > 500) {
      newErrors.menu = 'Menu must be 500 characters or less';
    }

    const guests = parseInt(formData.maxGuests, 10);
    if (!formData.maxGuests) {
      newErrors.maxGuests = 'Number of guests is required';
    } else if (isNaN(guests) || guests < 1) {
      newErrors.maxGuests = 'Must be at least 1 guest';
    } else if (guests > 8) {
      newErrors.maxGuests = 'Maximum 8 guests allowed';
    } else if (originalData?.currentGuests && guests < originalData.currentGuests) {
      newErrors.maxGuests = `Cannot set below ${originalData.currentGuests} (current bookings)`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDietaryChange = (option) => {
    setFormData(prev => {
      const current = prev.dietaryAccommodations;
      if (current.includes(option)) {
        return {
          ...prev,
          dietaryAccommodations: current.filter(item => item !== option)
        };
      } else {
        return {
          ...prev,
          dietaryAccommodations: [...current, option]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const editMeal = httpsCallable(functions, 'editMeal');
      await editMeal({
        mealId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        menu: formData.menu.trim(),
        maxGuests: parseInt(formData.maxGuests, 10),
        dietaryAccommodations: formData.dietaryAccommodations,
        alcoholOption: formData.alcoholOption,
        bookingCutoff: parseInt(formData.bookingCutoff, 10),
        specialNotes: formData.specialNotes.trim(),
      });
      navigate(`/meal-summary/${mealId}`);
    } catch (err) {
      console.error('Error updating meal:', err);
      setError(err.message || 'Failed to update meal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelListing = async () => {
    setSaving(true);
    try {
      const cancelMeal = httpsCallable(functions, 'cancelMeal');
      await cancelMeal({ mealId });
      navigate('/dashboard');
    } catch (err) {
      console.error('Error cancelling meal:', err);
      setError(err.message || 'Failed to cancel meal. Please try again.');
    } finally {
      setSaving(false);
      setShowCancelConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="create-listing-container">
        <div className="create-listing-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !originalData) {
    return (
      <div className="create-listing-container">
        <div className="create-listing-card">
          <div className="create-listing-error">{error}</div>
          <button
            className="submit-button"
            onClick={() => navigate('/host-dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-listing-container">
      <div className="create-listing-card">
        <h1>Edit Meal</h1>
        <p className="create-listing-subtitle">
          Update your meal details
        </p>

        {error && <div className="create-listing-error">{error}</div>}

        {/* Non-editable fields display */}
        <div className="readonly-section">
          <div className="readonly-field">
            <span className="readonly-label">Date & Time</span>
            <span className="readonly-value">{formatLongDateTime(originalData?.dateTime)}</span>
          </div>
          <div className="readonly-field">
            <span className="readonly-label">Location</span>
            <span className="readonly-value">
              {originalData?.neighborhood} - {originalData?.street}
              {originalData?.unitNumber && `, ${originalData.unitNumber}`}
            </span>
          </div>
          <div className="readonly-field">
            <span className="readonly-label">Cuisine Type</span>
            <span className="readonly-value">{originalData?.cuisineType}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="create-listing-form">
          <TitleField value={formData.title} error={errors.title} onChange={handleChange} />
          <DescriptionField value={formData.description} error={errors.description} onChange={handleChange} />
          <MenuField value={formData.menu} error={errors.menu} onChange={handleChange} />

          {/* Max Guests */}
          <div className="form-group">
            <label htmlFor="maxGuests">Max Guests</label>
            <input
              type="number"
              id="maxGuests"
              name="maxGuests"
              value={formData.maxGuests}
              onChange={handleChange}
              min={originalData?.currentGuests || 1}
              max={8}
              placeholder="Max 8 guests"
            />
            {originalData?.currentGuests > 0 && (
              <div className="char-count">
                {originalData.currentGuests} spots already booked
              </div>
            )}
            {errors.maxGuests && <span className="field-error">{errors.maxGuests}</span>}
          </div>

          {/* Booking Cutoff */}
          <div className="form-group">
            <label htmlFor="bookingCutoff">Booking Cutoff</label>
            <select
              id="bookingCutoff"
              name="bookingCutoff"
              value={formData.bookingCutoff}
              onChange={handleChange}
            >
              <option value="">Select booking cutoff time</option>
              {BOOKING_CUTOFF_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {errors.bookingCutoff && <span className="field-error">{errors.bookingCutoff}</span>}
          </div>

          <DietaryField selected={formData.dietaryAccommodations} onToggle={handleDietaryChange} />
          <AlcoholField value={formData.alcoholOption} onChange={handleChange} />
          <SpecialNotesField value={formData.specialNotes} onChange={handleChange} />

          <button
            type="submit"
            className="submit-button"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>

        {/* Cancel Listing Section */}
        <div className="cancel-section">
          {!showCancelConfirm ? (
            <button
              type="button"
              className="cancel-listing-button"
              onClick={() => setShowCancelConfirm(true)}
              disabled={saving}
            >
              Cancel Listing
            </button>
          ) : (
            <div className="cancel-confirm">
              <p>Are you sure you want to cancel this listing? This action cannot be undone.</p>
              <div className="cancel-confirm-buttons">
                <button
                  type="button"
                  className="cancel-confirm-yes"
                  onClick={handleCancelListing}
                  disabled={saving}
                >
                  {saving ? 'Cancelling...' : 'Yes, Cancel Listing'}
                </button>
                <button
                  type="button"
                  className="cancel-confirm-no"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={saving}
                >
                  No, Keep Listing
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditListing;
